import { useEffect, useRef } from "react";
import "react-tooltip/dist/react-tooltip.css";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setClinicalServiceName,
  setClinicalServiceSteps,
} from "../../../../redux/modules/clinical-services/actions";
import { setExpandedSection } from "../../../../redux/modules/medications/actions";
import { useParams } from "react-router-dom";
import { fetchNotepad } from "../../../../redux/modules/patient/actions";
import PrescriptionTabs from "../../clinical-services/service-steps/education/prescription-tabs";
import MedicationTable from "../../clinical-services/medications/medicationTable";
import PrescribedRxTable from "../../clinical-services/medications/prescribedRxTable";
import MedicationCreate from "../../clinical-services/service-steps/clinical-assessment/MedicationCreate";
import AddMedicationDialog from "../../clinical-services/medications/AddMedicationDialog";
import { drugTypes } from "../../clinical-services/service-steps/clinical-assessment/data";
import CustomInput from "../../../common/CustomInput";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../common/New-ToolTip";
import SubNavigation from "../../clinical-services/common/sub-navigation";
import { minorAilments } from "../../text/data";
// Using CustomInput for text, dropdown and date filters

const MedicationDashboard = ({ onActiveTabChange = () => { } }) => {
  const previousTabNameRef = useRef(null);
  const { patient } = useSelector((state) => state.patients);
  const { clinicalServiceObj, medications, clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );
  const { expandedSection, drugStatus, sortBy } = useSelector((state) => state.medications);

  const { id } = useParams();
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(true);
  
  // Define the static tabs
  const medicationTabs = [
    { name: "dashboard", label: "Medication Dashboard", comingSoon: true },
    { name: "list", label: "Medication List" },
    { name: "pending", label: "Rx - Pending Dispense" },
    { name: "history", label: "Medication History Log", comingSoon: true },
  ];

  // Set default tab to "Medication List"
  const [activeTab, setActiveTabState] = useState(medicationTabs[1]); // "list" tab
  
  // Wrapper function to prevent coming soon tabs from being activated
  const setActiveTab = (tab) => {
    if (tab?.comingSoon !== true) {
      setActiveTabState(tab);
    }
  };
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [medicationTypeFilter, setMedicationTypeFilter] = useState("all");
  const [medicationNameFilter, setMedicationNameFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [minorAilmentFilter, setMinorAilmentFilter] = useState("");
  // Medication create state
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [clickedMedication, setClickedMedication] = useState(null);

  // Add medication dialog state
  const [showAddMedicationDialog, setShowAddMedicationDialog] = useState(false);
  const [latestMedicationData, setLatestMedicationData] = useState(null);

  useEffect(() => {
    dispatch(fetchNotepad(id));
    dispatch(setClinicalServiceName(null));
    dispatch(setClinicalServiceSteps(null));
  }, [clinicalServiceObj]);

  // Open MedicationCreate when latestMedicationData is available
  useEffect(() => {
    if (latestMedicationData && !showMedicationSlider) {
      setShowMedicationSlider(true);
    }
  }, [latestMedicationData, showMedicationSlider]);

  // Prevent coming soon tabs from being active
  useEffect(() => {
    if (activeTab?.comingSoon === true) {
      // Reset to "list" tab if somehow a coming soon tab becomes active
      const listTab = medicationTabs.find(tab => tab.name === "list");
      if (listTab) {
        setActiveTabState(listTab);
      }
    }
  }, [activeTab]);

  // Notify parent component when activeTab changes
  useEffect(() => {
    onActiveTabChange(activeTab);
  }, [activeTab, onActiveTabChange]);

  // Default to Prescription only when switching into Medication List tab
  useEffect(() => {
    const previousTabName = previousTabNameRef.current;
    const currentTabName = activeTab?.name;
    const switchedIntoList = currentTabName === "list" && previousTabName !== "list";

    if (switchedIntoList && expandedSection !== "prescription") {
      dispatch(setExpandedSection("prescription"));
    }
    previousTabNameRef.current = currentTabName;
  }, [activeTab?.name, expandedSection, dispatch]);


  if (!patient) return null;

  // Status filter options
  const StatusOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Extract distinct minorAilmentTypeCode from clinicalServicesSmall.minorAilments
  // Only include items with status "refer"
  const getDistinctMinorAilmentCodes = () => {
    if (
      !clinicalServicesSmall?.minorAilments ||
      !Array.isArray(clinicalServicesSmall.minorAilments)
    ) {
      return [];
    }

    // Filter items with status "refer" and extract distinct codes
    const distinctCodes = [
      ...new Set(
        clinicalServicesSmall.minorAilments
          .filter((item) => item.status === "refer") // Only include items with status "refer"
          .map((item) => item.minorAilmentTypeCode)
      ),
    ].filter((code) => code); // Remove any null/undefined values

    return distinctCodes;
  };

  const distinctMinorAilmentCodes = getDistinctMinorAilmentCodes();

  // Build dynamic options for Minor Ailment Type from imported minorAilments
  // Filter based on distinct codes from clinicalServicesSmall
  const minorAilmentOptions = [
    { value: "", label: "Minor Ailment Type" },
    ...(Array.isArray(minorAilments)
      ? minorAilments
        .filter((item) => {
          // Filter out disabled items
          if (item.disable === true) return false;

          // If no distinct codes are available, show all enabled items
          if (distinctMinorAilmentCodes.length === 0) return true;

          // Check if the item's code exists in the distinct codes from clinicalServicesSmall
          return distinctMinorAilmentCodes.includes(item.code);
        })
        .map((item) => ({
          value: item.code,
          label: item.title,
        }))
      : []),
  ];

  // Helper function to check if medication matches date filter
  const matchesDateFilter = (med) => {
    if (!dateFilter) return true;

    const filterDate = new Date(dateFilter);
    const startDate = med.startDate ? new Date(med.startDate) : null;
    const endDate = med.endDate ? new Date(med.endDate) : null;

    // Check if filter date falls within the medication's date range
    if (startDate && endDate) {
      return filterDate >= startDate && filterDate <= endDate;
    } else if (startDate) {
      return filterDate >= startDate;
    } else if (endDate) {
      return filterDate <= endDate;
    }
    return true;
  };

  // Filter medications based on all criteria including Redux sidebar filters
  const filteredMedications =
    medications?.filter((med) => {
      const matchesName =
        !medicationNameFilter ||
        (med.medicationName &&
          med.medicationName
            .toLowerCase()
            .includes(medicationNameFilter.toLowerCase()));
      const matchesStatus =
        statusFilter === "all" || med.status === statusFilter;
      const matchesDate = matchesDateFilter(med);

      // Apply Redux sidebar filter (drugStatus)
      const matchesSidebarFilter = drugStatus === "all" || med.status === drugStatus;

      return matchesName && matchesStatus && matchesDate && matchesSidebarFilter;
    }) || [];

  // Apply sorting to filtered medications
  const sortMedications = (meds) => {
    // Safety check: ensure meds is an array
    if (!meds || !Array.isArray(meds)) {
      return [];
    }
    
    if (sortBy === "alphabetic") {
      return [...meds].sort((a, b) => (a?.medicationName || '').localeCompare(b?.medicationName || ''));
    } else if (sortBy === "newest") {
      return [...meds].sort((a, b) => new Date(b.created) - new Date(a.created));
    } else if (sortBy === "oldest") {
      return [...meds].sort((a, b) => new Date(a.created) - new Date(b.created));
    }
    return meds;
  };

  // Filter by medication type and apply sorting
  const prescriptionMedications = sortMedications(
    filteredMedications.filter((med) => med.medicationType === drugTypes.Prescription)
  );

  const additionalMedications = sortMedications(
    filteredMedications.filter((med) => med.medicationType === drugTypes.Additional)
  );

  // Helper function to get minor ailment code from medication
  const getMedicationMinorAilmentCode = (med) => {
    // Try different possible field names
    return med?.minorAilmentTypeCode || 
           med?.minorAilmentType || 
           med?.minorAilmentCode ||
           null;
  };

  // Debug: Log medication codes when filter is active
  useEffect(() => {
    if (minorAilmentFilter && minorAilmentFilter !== "" && minorAilmentFilter !== "all") {
      const prescribedRx = medications?.filter(med => 
        med.medicationType === drugTypes.PrescribedRx || med.medicationType === drugTypes.HistoryPrescribedRx
      ) || [];
   }
  }, [minorAilmentFilter, medications]);

  // Filter prescribed Rx medications with Redux filters and sorting
  const filteredPrescribedRx = medications?.filter(med => {
    const isPrescribedRx = med.medicationType === drugTypes.PrescribedRx || med.medicationType === drugTypes.HistoryPrescribedRx;

    // If filter is empty, null, undefined, or "all", show all records. Otherwise, filter by minorAilmentTypeCode
    let matchesAilment = true;
    if (minorAilmentFilter && minorAilmentFilter.trim() !== "" && minorAilmentFilter !== "all") {
      const medCode = getMedicationMinorAilmentCode(med);
      if (medCode) {
        // Compare codes (case-insensitive)
        matchesAilment = medCode.toLowerCase() === minorAilmentFilter.toLowerCase();
      } else {
        // If medication doesn't have a code, exclude it when filter is set
        matchesAilment = false;
      }
    }

    const matchesDate = matchesDateFilter(med);
    // Apply Redux sidebar filter (drugStatus)
    // For PrescribedRx, we might want to show all statuses, or filter based on drugStatus
    const matchesSidebarFilter = drugStatus === "all" || med.status === drugStatus || !med.status;

    return isPrescribedRx && matchesAilment && matchesDate && matchesSidebarFilter;
  }) || [];

  const prescribedRxMedications = sortMedications(filteredPrescribedRx);

  const handleAddMedicationDialogSave = (data) => {
    setLatestMedicationData(data);
    setShowAddMedicationDialog(false);
    // The useEffect will handle opening MedicationCreate when latestMedicationData is set
  };

  const handleAddMedicationDialogClose = () => {
    setShowAddMedicationDialog(false);
  };

  // Get header name based on active tab and filters
  const getHeaderName = () => {
    if (activeTab?.name === "list") {
      if (expandedSection === 'prescription' || (!expandedSection && (medicationTypeFilter === "all" || medicationTypeFilter === "prescription"))) {
        return "Prescription Medications";
      } else if (expandedSection === 'additional' || (!expandedSection && (medicationTypeFilter === "all" || medicationTypeFilter === "additional"))) {
        return "Additional Medications";
      }
      return "Prescription Medications"; // Default
    } else if (activeTab?.name === "pending") {
      return "Prescribed Rx - Pending Dispense";
    }
    return null; // No header for other tabs
  };

  const headerName = getHeaderName();

  const renderTabContent = () => {
    switch (activeTab?.name) {
      case "dashboard":
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Medication Dashboard
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
      case "list":
        return (
          <div className="w-full h-full overflow-x-auto">
            {/* Tables */}
            <div className="space-y-6">
              {/* Show Prescription table when prescription section is active or no specific section is active */}
              {(expandedSection === 'prescription' || !expandedSection) &&
                (medicationTypeFilter === "all" || medicationTypeFilter === "prescription") && (
                  <MedicationTable
                    headerName="Prescription Medications"
                    records={prescriptionMedications}
                    showHeader={false}
                  />
                )}

              {/* Show Additional table when additional section is active or no specific section is active */}
              {(expandedSection === 'additional' || !expandedSection) &&
                (medicationTypeFilter === "all" || medicationTypeFilter === "additional") && (
                  <MedicationTable
                    headerName="Additional Medications"
                    records={additionalMedications}
                    showHeader={false}
                  />
                )}
            </div>

          </div>
        );
      case "pending":
        return (
          <div className="flex flex-col w-full h-full gap-3 overflow-x-auto pt-[10px]">
            {/* Header row: title left, filters right */}
            <div className="flex items-center justify-between gap-6">
              <div className="flex gap-3 items-center">
                <h4 className="text-xl font-semibold text-teal-300">Prescribed Medication Record</h4>
              </div>
              <div className="flex gap-4">
                <div className="w-[280px]">
                  <CustomInput
                    options={minorAilmentOptions}
                    name="minorAilmentType"
                    className="w-full text-gray-800 font-semibold"
                    label=""
                    type="dropdown"
                    value={minorAilmentFilter}
                    onChange={(option) => {
                      // Handle both option object and direct value
                      const selectedValue = option?.value !== undefined ? option.value : (option || "");
                      setMinorAilmentFilter(selectedValue);
                    }}
                    placeholder="Minor Ailment Type"
                  />
                </div>
                <div className="w-[280px]">
                  <CustomInput
                    name="serviceDate"
                    className="w-full text-gray-800 font-semibold"
                    label=""
                    type="date"
                    value={dateFilter}
                    onChange={setDateFilter}
                    placeholder="Service Date"
                  />
                </div>
              </div>
            </div>

            {/* Prescriptions Prescribed Table */}
            <div>
              <PrescribedRxTable
                headerName="Prescribed Medication Record"
                records={prescribedRxMedications}
              />
            </div>
          </div>
        );
      case "history":
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Medication History
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
      default:
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Medication Dashboard
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col flex-1 max-h-[802px] gap-[10px] w-full overflow-hidden">
      {/* Header - Above Tabs */}
      {headerName && (
        <div className="flex justify-between items-center pb-[10px] border-b-[7px] border-gray-20">
          <div className="flex gap-3 items-center">
            <h4 className="text-[22px] font-bold justify-center text-teal-300 px-[5px]">
              {headerName}
            </h4>
            <ReactSVG
              src="/icons/information-dark.svg"
              className="w-5 h-5"
              data-tooltip-id={`medication-table-info-${headerName?.toLowerCase().replace(/\s+/g, '-')}`}
            />
            <NewToolTip
              id={`medication-table-info-${headerName?.toLowerCase().replace(/\s+/g, '-')}`}
              render={() => {
                const isPrescription = headerName?.toLowerCase().includes('prescription') && !headerName?.toLowerCase().includes('pending');
                const isAdditional = headerName?.toLowerCase().includes('additional');
                const isPending = headerName?.toLowerCase().includes('pending');

                return (
                  <div className="bg-gray-10 p-3 flex flex-col max-w-[400px]">
                    {isPending ? (
                      <><p className="text-teal-300 text-sm font-semibold mb-1">Prescribed Rx – Pending Dispense</p>
                        <p className="text-teal-300 text-sm font-normal">
                          Prescriptions issued during a Minor Ailment assessment, but not yet confirmed as dispensed. Status will update automatically once pharmacy system integration is enabled.
                        </p>
                      </>
                    ) : isPrescription ? (
                      <>
                        <p className="text-teal-300 text-sm font-semibold mb-1">Prescription Medication</p>
                        <p className="text-teal-300 text-sm font-normal">Medications filled at the current pharmacy.</p>
                      </>
                    ) : isAdditional ? (
                      <>
                        <p className="text-teal-300 text-sm font-semibold mb-1">Additional Medication</p>
                        <p className="text-teal-300 text-sm font-normal">Includes other prescriptions filled elsewhere,
                          medical study drugs, OTCs, samples and natural products.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-teal-300 text-sm font-semibold mb-1">Prescription Medication</p>
                        <p className="text-teal-300 text-sm font-normal mb-2">Medications filled at the current pharmacy.</p>
                        <p className="text-teal-300 text-sm font-semibold mb-1">Additional Medication</p>
                        <p className="text-teal-300 text-sm font-normal">Includes other prescriptions filled elsewhere,
                          medical study drugs, OTCs, samples and natural products.</p>
                      </>
                    )}
                  </div>
                );
              }}
            />
          </div>
          <SubNavigation />
        </div>
      )}

      {/* Header Tabs */}
      <PrescriptionTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={medicationTabs}
      />

      {/* Tab Content */}
      <div className="flex-1 bg-white overflow-y-auto">
        {renderTabContent()}
      </div>

      {/* Add Medication Dialog */}
      <AddMedicationDialog
        open={showAddMedicationDialog}
        onClose={handleAddMedicationDialogClose}
        onSave={(data) => handleAddMedicationDialogSave(data)}
        patientId={id}
      />

      <MedicationCreate
        create={clickedMedication?.id ? false : true}
        patientId={id}
        type="Medication"
        open={showMedicationSlider}
        fullMedication={clickedMedication}
        initialData={latestMedicationData}
        step={1}
        maxSteps={1}
        onClose={(value) => {
          setClickedMedication(null);
          setLatestMedicationData(null);
          setShowMedicationSlider(value);
        }}
        onSave={() => {
          setShowMedicationSlider(false);
          setLatestMedicationData(null);
        }}
      />
    </div>
  );
};

export default MedicationDashboard;
